/**
 * Куда вести человека со входа (`/`, `/dashboard`) — чистое правило (AK-896;
 * AK-866 В-3, макет v2 экран 3, сценарии UC-1/UC-2/UC-7). Тестируется без
 * React и сети; `DashboardEntry` только исполняет результат.
 *
 * Приоритет источников хозяйства — от самого явного к догадке:
 *
 *   1. `?farm=` в адресе — присланная ссылка (UC-7). Обязана открыть именно
 *      своё хозяйство; `useFarmDeepLink` уже переключает область (один
 *      `POST /scope/set`), здесь его результат только читается.
 *   2. Область сессии типа «хозяйство» — то, что стоит в переключателе.
 *   3. Память «последней области» (`lastScope`) — выбор человека в прошлый раз,
 *      если хозяйство ещё доступно; «Холдинг» из памяти — тоже выбор.
 *   4. Единственное доступное хозяйство — выбирать не из чего (UC-1).
 *   5. Кластер/отделение в сессии — конкретика, которую мог выбрать только
 *      человек (карта); не перебиваем догадкой — `/farm` попросит выбрать.
 *   6. Первый вход роли компании (есть company-строка) — «Холдинг».
 *   7. Первый вход остальных — первое хозяйство по алфавиту.
 *
 * Пункты 3 и 7 пишут область сессии (`write: true`): иначе человек стоит на
 * экране хозяйства X, а переключатель показывает «Холдинг». Пункт 4 пишет
 * best-effort (область уже совпадает у роли хозяйства — `useScopeSetter` сеть
 * не трогает; у члена кластера с одним хозяйством — выровняет сессию). Пункт 6
 * не пишет: широкий `current` и так рисует «Холдинг».
 *
 * Правило стартового экрана при хозяйстве — `startScreenFor`: двойник
 * заполнен (`done_count` = `total_steps`) → «Карта», иначе → «Данные
 * хозяйства» с полосой. Старый формат ручки (нет `done_count`) и ошибка →
 * «не заполнен»: безопасный дефолт — экран, где видно, чего не хватает, а не
 * карта без контекста.
 */
import type { AvailableScopes, ScopeInfo } from './ScopeTree';
import type { LastScope } from './lastScope';
import type { FarmDeepLink } from './useFarmDeepLink';

export type EntrySource = 'deep-link' | 'session' | 'last' | 'only' | 'first';

export type EntryTarget =
  /** `/scope/available` или ссылка ещё не разобраны — «Загружаем…». */
  | { kind: 'loading' }
  /** Ссылка ведёт в хозяйство не из доступных — экран «Нет доступа». */
  | { kind: 'no-access'; farmId: string }
  /** Сервер отказал переключить область по ссылке — ошибка с «Повторить». */
  | { kind: 'link-error'; farmId: string; message: string; retry: () => void }
  /** Экран «Холдинг» (`DashboardPage`) — в т.ч. когда областей нет вовсе. */
  | { kind: 'holding' }
  /** `/farm` — попросить выбрать хозяйство в переключателе. */
  | { kind: 'pick' }
  | {
      kind: 'farm';
      farmId: string;
      source: EntrySource;
      /**
       * Записать область сессии этим хозяйством (`POST /scope/set`), если
       * она ещё другая. Для `deep-link` — `false`: пишет сам `useFarmDeepLink`.
       */
      write: boolean;
    };

export interface ResolveEntryInput {
  /** Ответ `/scope/available` штатной формы; `undefined` — нет/не того формата. */
  data: AvailableScopes | undefined;
  /** Список не загрузился или пришёл не того формата — решать не по чему. */
  scopesFailed: boolean;
  link: FarmDeepLink;
  last: LastScope | null;
}

const byName = (a: ScopeInfo, b: ScopeInfo) => a.name.localeCompare(b.name, 'ru');

export function resolveEntryTarget({ data, scopesFailed, link, last }: ResolveEntryInput): EntryTarget {
  // Списка нет: ошибка/заглушка → дашборд сам покажет «Повторить»/онбординг
  // (как AK-898); иначе ждём — судить о ссылке и о хозяйстве рано.
  if (!data) return scopesFailed ? { kind: 'holding' } : { kind: 'loading' };

  // 1. Ссылка.
  switch (link.state) {
    case 'no-access':
      return { kind: 'no-access', farmId: link.farmId };
    case 'error':
      return { kind: 'link-error', farmId: link.farmId, message: link.message, retry: link.retry };
    case 'switching':
      return { kind: 'farm', farmId: link.farmId, source: 'deep-link', write: false };
    case 'waiting':
      return { kind: 'loading' };
    case 'none':
      break;
  }

  const farms = data.available.filter((s) => s.scope_type === 'farm' && s.scope_id).sort(byName);
  const companyRow = data.available.find((s) => s.scope_type === 'company');
  if (farms.length === 0 && !companyRow) return { kind: 'holding' };

  // 2. Хозяйство уже в переключателе.
  const cur = data.current;
  if (cur.scope_type === 'farm' && cur.scope_id) {
    return { kind: 'farm', farmId: cur.scope_id, source: 'session', write: false };
  }

  // 3. Выбор в прошлый раз.
  if (last?.kind === 'farm' && farms.some((f) => f.scope_id === last.farmId)) {
    return { kind: 'farm', farmId: last.farmId, source: 'last', write: true };
  }
  if (last?.kind === 'holding' && companyRow) return { kind: 'holding' };

  // 4. Выбирать не из чего.
  if (farms.length === 1) {
    return { kind: 'farm', farmId: farms[0].scope_id as string, source: 'only', write: true };
  }

  // 5. Конкретика в сессии, которую не перебиваем догадкой: кластер — экран
  //    сводки кластера (AK-903: та же страница, что «Холдинг», линза кластера
  //    режет выборку сама); отделение — просьба выбрать хозяйство.
  if (cur.scope_type === 'cluster') return { kind: 'holding' };
  if (cur.scope_type === 'brigade') return { kind: 'pick' };

  // 6. Первый вход роли компании.
  if (companyRow) return { kind: 'holding' };

  // 7. Первый вход остальных.
  if (farms.length > 0) {
    return { kind: 'farm', farmId: farms[0].scope_id as string, source: 'first', write: true };
  }
  return { kind: 'holding' };
}

export type StartScreen = 'data' | 'map';

/** Срез ответа `GET /farms/{id}/preparation`, нужный правилу. */
export interface TwinCompletion {
  done_count?: number;
  total_steps?: number;
}

/**
 * Двойник заполнен → «Карта»; иначе (в т.ч. старый формат без `done_count`,
 * ошибка ручки, `total_steps = 0`) → «Данные хозяйства».
 */
export function startScreenFor(prep: TwinCompletion | null | undefined): StartScreen {
  if (!prep) return 'data';
  const { done_count, total_steps } = prep;
  if (typeof done_count !== 'number' || typeof total_steps !== 'number') return 'data';
  if (total_steps <= 0) return 'data';
  return done_count >= total_steps ? 'map' : 'data';
}
